/**
 * Client-side image compression utility using the Canvas API.
 * Used before uploading school assets (question diagrams, school logo) to S3.
 */

const MAX_DIMENSION = 1600; // px — scale down if wider/taller than this
const MIN_QUALITY   = 0.3;  // stop compression at 30% quality
const QUALITY_STEP  = 0.1;

/**
 * Compresses an image File if it exceeds maxSizeMB.
 * Uses JPEG output for best size reduction.
 *
 * @param {File}   file        - Input image file
 * @param {number} maxSizeMB   - Maximum allowed size in MB (default 2)
 * @returns {Promise<{ file: File, compressed: boolean }>}
 *   file       — original if within limit, otherwise compressed File
 *   compressed — true if compression was applied
 */
export async function compressImageIfNeeded(file, maxSizeMB = 2) {
    const maxBytes = maxSizeMB * 1024 * 1024;

    if (file.size <= maxBytes) {
        return { file, compressed: false };
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(new Error('Failed to read image file'));

        reader.onload = (evt) => {
            const img = new Image();

            img.onerror = () => reject(new Error('Failed to decode image'));

            img.onload = () => {
                // Determine output dimensions
                let { width, height } = img;
                if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                    if (width >= height) {
                        height = Math.round((height * MAX_DIMENSION) / width);
                        width  = MAX_DIMENSION;
                    } else {
                        width  = Math.round((width * MAX_DIMENSION) / height);
                        height = MAX_DIMENSION;
                    }
                }

                const canvas  = document.createElement('canvas');
                canvas.width  = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // Iteratively reduce quality until under limit
                let quality = 0.85;

                const tryCompress = () => {
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Canvas compression failed'));
                            return;
                        }

                        if (blob.size <= maxBytes || quality <= MIN_QUALITY) {
                            // Produce a File from the blob
                            const outName = file.name.replace(/\.[^.]+$/, '.jpg');
                            const compressed = new File([blob], outName, { type: 'image/jpeg' });
                            resolve({ file: compressed, compressed: true });
                        } else {
                            quality = Math.max(MIN_QUALITY, quality - QUALITY_STEP);
                            tryCompress();
                        }
                    }, 'image/jpeg', quality);
                };

                tryCompress();
            };

            img.src = evt.target.result;
        };

        reader.readAsDataURL(file);
    });
}

/**
 * Converts any image file to JPEG format using the Canvas API.
 * Ensures PDF compatibility (pdfkit only supports JPEG/PNG).
 *
 * @param {File}   file     - Input image file (WebP, GIF, PNG, JPEG, etc.)
 * @param {number} quality  - JPEG quality 0–1 (default 0.92)
 * @returns {Promise<File>} JPEG File
 */
export async function convertToJpeg(file, quality = 0.92) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.onload = evt => {
            const img = new Image();
            img.onerror = () => reject(new Error('Failed to decode image'));
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width  = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d').drawImage(img, 0, 0);
                canvas.toBlob(blob => {
                    if (!blob) { reject(new Error('Canvas conversion failed')); return; }
                    const outName = file.name.replace(/\.[^.]+$/, '.jpg');
                    resolve(new File([blob], outName, { type: 'image/jpeg' }));
                }, 'image/jpeg', quality);
            };
            img.src = evt.target.result;
        };
        reader.readAsDataURL(file);
    });
}

/**
 * Validates an image file before upload.
 * @param {File} file
 * @param {number} maxSizeMB
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateImageFile(file, maxSizeMB = 2) {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
        return { valid: false, error: 'Only JPEG, PNG, WebP, or GIF images are allowed.' };
    }
    // We compress if too big, but GIFs can't be reliably compressed with canvas
    if (file.type === 'image/gif' && file.size > maxSizeMB * 1024 * 1024) {
        return { valid: false, error: `GIF must be under ${maxSizeMB} MB.` };
    }
    return { valid: true };
}
